import { schemaPropertiesToValues } from "components/FormerEditor/common";
import { getFocusForContext } from "utils/hooks/focus";
import startCase from "lodash/startCase";
import get from "lodash/get";
import { applyEditorFocus } from "utils/builder";
import {
  CHECKOUT_PRODUCT_SETTINGS_INTENT,
  CHECKOUT_SETTINGS_INTENT
} from "utils/constants/checkout";
import { EDITOR_MODE } from "utils/constants/editor";
import { FOCUS_CHECKOUT_KEY } from "utils/constants/focus";
import { ENTITY_TYPE_LABEL_MAP, FIELD_TYPES } from "utils/constants/form";
import { VIEWPORT_SIZE } from "utils/constants/viewport";
import {
  getFocusConfig,
  getFormFieldGroupFieldFocus,
  getEditorModeFocus,
  getEditorViewportFocus,
  getFormCheckoutSettingsFocus
} from "../focus";
import { v4 as uuidv4 } from "uuid";
import { addModel } from "utils/drag";
import { STATE_KEYS } from "utils/constants/state";
import { FORM_INTENT } from "utils/constants/pricing";
import { BUILDER_ENTITY, BUILDER_INTENT } from "utils/constants/builder";
import { APP_PUBLISH_TAB } from "utils/constants/application";
import { extendQS } from "utils/route";
import { getSchemaValuesForEditor } from "utils/schema";

const CHECKOUT = "checkout";
const COMBINATIONS = "combinations";

const CONTROLS = "controls";
const MODE = "mode";
const DEVICE = "device";

const FORM = "form";
const FIELDS = "fields";
const FIELD_GROUPS = "field_groups";
const ADD_FIELD = "add_field";
const ADD_FIELD_GROUP = "add_field_group";

const PRESENTATION = "presentation";
const THEME = "theme";

const OTHER = "other";
const PUBLISH = "publish";
const EMBED = "embed";
const SAVE = "save";

const TUTORIALS = "tutorials";

const FIELD_KEYWORDS = {
  [FIELD_TYPES.TIN]: ["tax id"],
  [FIELD_TYPES.PHONE]: ["phone"],
  [FIELD_TYPES.SELECT]: ["select"],
  [FIELD_TYPES.MULTISELECT]: ["multiselect"],
  [FIELD_TYPES.INPUT]: ["input", "text"],
  [FIELD_TYPES.TEXTAREA]: ["textarea"],
  [FIELD_TYPES.TOGGLE]: ["toggle", "switch"],
  [FIELD_TYPES.CHECKBOX]: ["checkbox"],
  [FIELD_TYPES.DISCLAIMER]: ["disclaimer"],
  [FIELD_TYPES.DATEPICKER]: ["datepicker", "date"],
  [FIELD_TYPES.FILE]: ["file", "attachment"],
  [FIELD_TYPES.IDENTITY]: ["identity", "verification"],
  [FIELD_TYPES.ADDRESS]: ["address", "location"],
  [FIELD_TYPES.LEGAL]: ["legal", "terms and conditions"]
};

export const COMMAND_ITEM_ID = {
  CHECKOUT: {
    DEFAULT: `${CHECKOUT}_default`,
    COMMON: `${CHECKOUT}_common`,
    PRODUCTS: `${CHECKOUT}_products`,
    PRODUCTS_INDEX: `${CHECKOUT}_products_index`,
    PRODUCTS_FIELDS: `${CHECKOUT}_products_fields`,
    PRODUCTS_SETTINGS: `${CHECKOUT}_products_settings`
  },
  CONTROLS: {
    MODE: `${CONTROLS}_${MODE}`,
    MODE_USE: `${CONTROLS}_${MODE}_${EDITOR_MODE.USE}`,
    MODE_INSPECT: `${CONTROLS}_${MODE}_${EDITOR_MODE.INSPECT}`,
    MODE_EDIT: `${CONTROLS}_${MODE}_${EDITOR_MODE.EDIT}`,
    DEVICE: `${CONTROLS}_${DEVICE}`,
    DEVICE_MOBILE: `${CONTROLS}_${DEVICE}_${VIEWPORT_SIZE.MOBILE}`,
    DEVICE_DESKTOP: `${CONTROLS}_${DEVICE}_${VIEWPORT_SIZE.DESKTOP}`
  },
  FORM: {
    ADD_FIELD: `${FORM}_${ADD_FIELD}`,
    ADD_FIELD_GROUP: `${FORM}_${ADD_FIELD_GROUP}`,
    FIELDS: `${FORM}_${FIELDS}`,
    FIELD_GROUPS: `${FORM}_${FIELD_GROUPS}`
  },
  OTHER: {
    PRESENTATION: `${OTHER}_${PRESENTATION}`,
    THEME: `${OTHER}_${THEME}`,
    PUBLISH: `${OTHER}_${PUBLISH}`,
    EMBED: `${OTHER}_${EMBED}`,
    SAVE: `${OTHER}_${SAVE}`,
    TUTORIALS: `${OTHER}_${TUTORIALS}`
  },
  TUTORIALS: {
    CHECKOUT: `${TUTORIALS}_${CHECKOUT}`
  }
};

const getCheckoutCombinationsConfig = ({
  combinations,
  commonContext,
  commonFocus
}) => {
  const parent = {
    id: COMMAND_ITEM_ID.CHECKOUT.PRODUCTS,
    name: "Logic",
    keywords: "combinations logic",
    section: CHECKOUT
  };
  const fieldsChildren = [
    {
      id: COMMAND_ITEM_ID.CHECKOUT.PRODUCTS_FIELDS,
      parent: parent.id,
      name: "Fields",
      perform: applyEditorFocus({
        ...commonFocus,
        focus: getFormCheckoutSettingsFocus({
          uuid: commonContext.uuid,
          menuSubIntent: CHECKOUT_SETTINGS_INTENT.SET_PRODUCTS,
          menuThirdIntent: CHECKOUT_PRODUCT_SETTINGS_INTENT.SET_FIELDS
        })
      })
    }
  ];

  let comboChildren = [];
  if (Array.isArray(combinations) && combinations.length) {
    const commonComboChildConfig = {
      section: COMBINATIONS,
      parent: parent.id
    };

    comboChildren = combinations.reduce(
      (memo, { uuid }, comboIx) => {
        memo.push({
          ...commonComboChildConfig,
          id: uuid,
          name: comboIx + 1,
          //   subtitle: "Conditional checkout configuration.",
          //   keywords: "combinations logic",
          perform: applyEditorFocus({
            ...commonFocus,
            focus: getFocusForContext({
              ...commonContext,
              context: FOCUS_CHECKOUT_KEY.COMBINATIONS,
              activeIndex: comboIx
            })
          })
        });
        return memo;
      },
      [
        {
          ...commonComboChildConfig,
          id: COMMAND_ITEM_ID.CHECKOUT.PRODUCTS_INDEX,
          name: "All",
          perform: applyEditorFocus({
            ...commonFocus,
            focus: getFocusForContext({
              ...commonContext,
              context: FOCUS_CHECKOUT_KEY.COMBINATIONS
            })
          })
        }
      ]
    );
  }
  const settingsChildren = [
    {
      id: COMMAND_ITEM_ID.CHECKOUT.PRODUCTS_SETTINGS,
      parent: parent.id,
      name: "Settings",
      perform: applyEditorFocus({
        ...commonFocus,
        focus: getFormCheckoutSettingsFocus({
          uuid: commonContext.uuid,
          menuSubIntent: CHECKOUT_SETTINGS_INTENT.SET_PRODUCTS,
          menuThirdIntent: CHECKOUT_PRODUCT_SETTINGS_INTENT.SET_SETTINGS
        })
      })
    }
  ];

  return [parent, ...fieldsChildren, ...comboChildren, ...settingsChildren];
};

export const getCheckoutConfig = ({
  combinations,
  commonContext,
  commonFocus
}) => {
  const checkoutCombinationsConfig = getCheckoutCombinationsConfig({
    combinations,
    commonContext,
    commonFocus
  });
  return [
    {
      id: COMMAND_ITEM_ID.CHECKOUT.DEFAULT,
      name: "Default",
      keywords: "default",
      section: CHECKOUT,
      perform: applyEditorFocus({
        ...commonFocus,
        focus: getFocusForContext({
          ...commonContext,
          context: FOCUS_CHECKOUT_KEY.CONSTANT
        })
      })
    },
    ...checkoutCombinationsConfig,
    {
      id: COMMAND_ITEM_ID.CHECKOUT.COMMON,
      name: "Common",
      keywords: "common shared",
      section: CHECKOUT,
      perform: applyEditorFocus({
        ...commonFocus,
        focus: getFocusForContext({
          ...commonContext,
          context: FOCUS_CHECKOUT_KEY.COMMON
        })
      })
    }
  ];
};

export const getControlsConfig = ({ commonFocus }) => {
  return [
    {
      id: COMMAND_ITEM_ID.CONTROLS.MODE,
      name: "Mode",
      keywords: "edit click use inspect",
      section: CONTROLS
    },
    ...[
      {
        id: COMMAND_ITEM_ID.CONTROLS.MODE_USE,
        name: startCase(EDITOR_MODE.USE),
        parent: COMMAND_ITEM_ID.CONTROLS.MODE,
        keywords: EDITOR_MODE.USE,
        perform: applyEditorFocus({
          ...commonFocus,
          focus: getEditorModeFocus(EDITOR_MODE.USE)
        })
      },
      {
        id: COMMAND_ITEM_ID.CONTROLS.MODE_INSPECT,
        name: startCase(EDITOR_MODE.INSPECT),
        parent: COMMAND_ITEM_ID.CONTROLS.MODE,
        keywords: EDITOR_MODE.INSPECT,
        perform: applyEditorFocus({
          ...commonFocus,
          focus: getEditorModeFocus(EDITOR_MODE.INSPECT)
        })
      },
      {
        id: COMMAND_ITEM_ID.CONTROLS.MODE_EDIT,
        name: startCase(EDITOR_MODE.EDIT),
        parent: COMMAND_ITEM_ID.CONTROLS.MODE,
        keywords: EDITOR_MODE.EDIT,
        perform: applyEditorFocus({
          ...commonFocus,
          focus: getEditorModeFocus(EDITOR_MODE.EDIT)
        })
      }
    ],
    {
      id: COMMAND_ITEM_ID.CONTROLS.DEVICE,
      name: "Device",
      keywords: "edit click use inspect",
      section: CONTROLS
    },
    ...[
      {
        id: COMMAND_ITEM_ID.CONTROLS.DEVICE_MOBILE,
        name: startCase(VIEWPORT_SIZE.MOBILE),
        parent: COMMAND_ITEM_ID.CONTROLS.DEVICE,
        keywords: VIEWPORT_SIZE.MOBILE,
        perform: applyEditorFocus({
          ...commonFocus,
          focus: getEditorViewportFocus(VIEWPORT_SIZE.MOBILE)
        })
      },
      {
        id: COMMAND_ITEM_ID.CONTROLS.DEVICE_DESKTOP,
        name: startCase(VIEWPORT_SIZE.DESKTOP),
        parent: COMMAND_ITEM_ID.CONTROLS.DEVICE,
        keywords: VIEWPORT_SIZE.DESKTOP,
        perform: applyEditorFocus({
          ...commonFocus,
          focus: getEditorViewportFocus(VIEWPORT_SIZE.DESKTOP)
        })
      }
    ]
  ];
};

const getFieldsNodesConfig = ({
  fieldGroups,
  entities,
  commonContext,
  commonFocus
}) => {
  const fields = fieldGroups.reduce((memo, fieldGroup) => {
    if (Array.isArray(fieldGroup.fields) && fieldGroup.fields.length) {
      fieldGroup.fields.forEach((field) => {
        memo.push(field);
      });
    }

    return memo;
  }, []);

  let showFieldsConfig = [];
  if (fields.length) {
    showFieldsConfig = [
      {
        id: COMMAND_ITEM_ID.FORM.FIELDS,
        name: "Show field",
        keywords: "show",
        section: FORM
      },
      ...fieldGroups.reduce((memo, fieldGroup, fieldGroupIx) => {
        if (Array.isArray(fieldGroup.fields) && fieldGroup.fields.length) {
          fieldGroup.fields.forEach((field, fieldIx) => {
            const fieldName = field.label || startCase(field.uid);
            memo.push({
              id: field.uuid,
              name: field.label || startCase(field.uid),
              keywords: ["show", fieldName].join(" "),
              parent: COMMAND_ITEM_ID.FORM.FIELDS,
              perform: applyEditorFocus({
                ...commonFocus,
                focus: getFormFieldGroupFieldFocus({
                  uuid: commonContext.uuid,
                  mode: EDITOR_MODE.INSPECT,
                  activeIndex: fieldGroupIx,
                  activeSubIndex: fieldIx
                })
              })
            });
          });
        }
        return memo;
      }, [])
    ];
  }

  return [
    {
      id: COMMAND_ITEM_ID.FORM.ADD_FIELD,
      name: "Add field",
      keywords: "add",
      section: FORM
    },
    ...Object.keys(entities).reduce((memo, entityKey) => {
      const collection = entities[entityKey];
      if (Array.isArray(collection) && collection.length) {
        collection.forEach((entity) => {
          const fieldKeywords = FIELD_KEYWORDS[entity.uid];
          memo.push({
            id: entity.uid,
            name: entity.label,
            keywords: [
              ...fieldKeywords,
              fieldKeywords.map((keyword) => `add ${keyword}`)
            ].join(" "),
            section: ENTITY_TYPE_LABEL_MAP[entity.type],
            parent: COMMAND_ITEM_ID.FORM.ADD_FIELD,
            perform: () => {
              const schemaValues = schemaPropertiesToValues(
                entity.schema.properties
              );
              const scaffoldedField = {
                uuid: uuidv4(),
                uid: entity.uid,
                ...schemaValues
              };

              const modelsPath = `manifest.data.content.${commonContext.uuid}.data.fieldGroups[0].fields`;
              const models = get(commonFocus.values, modelsPath) || [];
              const nextIndex = models.length;

              addModel({
                ...commonFocus,
                template: scaffoldedField,
                modelsPath: modelsPath,
                stateKey: STATE_KEYS.EDITOR.MENU_ACTIVE_SUB_INDEX
              })();

              applyEditorFocus({
                ...commonFocus,
                focus: getFormFieldGroupFieldFocus({
                  uuid: commonContext.uuid,
                  mode: EDITOR_MODE.INSPECT,
                  activeIndex: 0,
                  activeSubIndex: nextIndex
                })
              })();
            }
          });
        });
      }
      return memo;
    }, []),
    ...showFieldsConfig
  ];
};

const getFieldGroupsNodesConfig = ({
  fieldGroups,
  commonContext,
  commonFocus,
  schema
}) => {
  let showFieldGroupsConfig = [];
  if (fieldGroups.length) {
    showFieldGroupsConfig = [
      {
        id: COMMAND_ITEM_ID.FORM.FIELD_GROUPS,
        name: "Show field group",
        keywords: "show",
        section: FORM
      },
      ...fieldGroups.map((fieldGroup, fieldGroupIx) => {
        return {
          id: fieldGroup.uuid,
          name: fieldGroup.title || `Field group ${fieldGroupIx + 1}`,
          keywords: "add",
          parent: COMMAND_ITEM_ID.FORM.FIELD_GROUPS,
          perform: applyEditorFocus({
            ...commonFocus,
            focus: getFormFieldGroupFieldFocus({
              uuid: commonContext.uuid,
              mode: EDITOR_MODE.INSPECT,
              activeIndex: fieldGroupIx
            })
          })
        };
      })
    ];
  }

  return [
    {
      id: COMMAND_ITEM_ID.FORM.ADD_FIELD_GROUP,
      name: "Add field group",
      keywords: "add",
      section: FORM,
      perform: () => {
        const { template } = getSchemaValuesForEditor({
          schema,
          propertyKey: "fieldGroups"
        });

        const modelsPath = `manifest.data.content.${commonContext.uuid}.data.fieldGroups`;
        const models = get(commonFocus.values, modelsPath) || [];
        const nextIndex = models.length;

        addModel({
          ...commonFocus,
          template,
          modelsPath: modelsPath,
          stateKey: STATE_KEYS.EDITOR.MENU_ACTIVE_INDEX
        })();

        applyEditorFocus({
          ...commonFocus,
          focus: getFormFieldGroupFieldFocus({
            uuid: commonContext.uuid,
            mode: EDITOR_MODE.INSPECT,
            activeIndex: nextIndex
          })
        })();
      }
    },
    ...showFieldGroupsConfig
  ];
};

export const getFormConfig = ({
  fieldGroups,
  entities,
  commonContext,
  commonFocus,
  schema
}) => {
  const fieldsNodes = getFieldsNodesConfig({
    fieldGroups,
    entities,
    commonContext,
    commonFocus
  });

  const fieldGroupsNodes = getFieldGroupsNodesConfig({
    fieldGroups,
    commonContext,
    commonFocus,
    schema
  });

  return [...fieldsNodes, ...fieldGroupsNodes];
};

export const getOtherConfig = ({ commonFocus, commonContext, router }) => {
  return [
    {
      id: COMMAND_ITEM_ID.OTHER.PRESENTATION,
      name: "Presentation",
      section: OTHER,
      keywords: ["brand", "logo"].join(","),
      perform: applyEditorFocus({
        ...commonFocus,
        focus: getFocusConfig({
          uuid: commonContext.uuid,
          mode: EDITOR_MODE.INSPECT,
          menuIntent: FORM_INTENT.PRESENTATION_SETTINGS
        })
      })
    },
    {
      id: COMMAND_ITEM_ID.OTHER.THEME,
      name: "Theme",
      section: OTHER,
      keywords: ["style", "color"].join(","),
      perform: applyEditorFocus({
        ...commonFocus,
        focus: [
          { key: STATE_KEYS.EDITOR.INTENT, value: BUILDER_INTENT.PAGE_THEME },
          {
            key: STATE_KEYS.EDITOR.VISIBILITY_PANEL,
            value: true
          },
          {
            key: STATE_KEYS.EDITOR.ACTIVE_BLOCK,
            value: BUILDER_ENTITY.PAGE_THEME
          }
        ]
      })
    },
    {
      id: COMMAND_ITEM_ID.OTHER.PUBLISH,
      name: "Publish",
      section: OTHER,
      keywords: ["live", "page"].join(","),
      perform: () => {
        applyEditorFocus({
          ...commonFocus,
          focus: [{ key: STATE_KEYS.EDITOR.PUBLISH_SHOW_MENU, value: true }]
        })();

        router.push(
          extendQS(router.asPath, {
            tab: APP_PUBLISH_TAB.LOCATION
          })
        );
      }
    },
    {
      id: COMMAND_ITEM_ID.OTHER.EMBED,
      name: "Embed",
      section: OTHER,
      keywords: ["code", "html"].join(","),
      perform: () => {
        applyEditorFocus({
          ...commonFocus,
          focus: [{ key: STATE_KEYS.EDITOR.PUBLISH_SHOW_MENU, value: true }]
        })();

        router.push(
          extendQS(router.asPath, {
            tab: APP_PUBLISH_TAB.EMBED
          })
        );
      }
    }
  ];
};
